import type { FloraDefinition } from '@/flora/FloraDefinition';
import type { SimpleSeason } from '@/environment/EnvironmentState';
import { SEASON_ANCHORS } from '@/environment/SeasonModel';
import { FloraPreview, PERSON_HEIGHT_METERS, frameSubject } from '@/rendering/FloraPreview';
import { append, clear, el, formatLength, type Child } from './dom';
import { filledIcon, icon } from './Icons';
import { floraThumbnailSvg, safeColor } from './FloraThumbnail';
import type { UiPreferences } from './UiPreferences';
import type { FloraResolver } from '@/localization/FloraResolver';
import {
  buildFloraFactsheet,
  numberLabel,
  percentLabel,
  type Fact,
  type FactSection,
  type FloraFactsheet,
  type SeasonFacet,
} from './FloraFactsheet';

/**
 * The plant faceplate — the whole catalog record for one species, opened from the ⓘ on its tile.
 *
 * A palette tile can only ever show the three things you browse on. Everything else the record
 * carries is design data: the nursery stages you order from, the curves the plant grows along, the
 * four seasonal palettes, the phenology days, the LOD distances. This card is where
 * that lives, and it shows it *as stored* — a field the record omits is drawn greyed rather than
 * quietly defaulted, and the raw JSON is one disclosure away at the bottom.
 *
 * It opens beside the palette rather than over it, so the tiles stay clickable and you can walk the
 * catalog with the card following along. It is non-modal for the same reason the manifest is: the
 * scene behind it is the thing you are comparing against.
 *
 * Layout is the only job here. Every figure on the card comes from `FloraFactsheet.ts`, and the
 * silhouettes are the same `floraThumbnailSvg` the tiles draw, so nothing on this sheet can drift
 * from what the palette and the viewport show.
 */

export interface FloraCardDeps {
  /**
   * Where the card mounts. It positions itself against the viewport, so the host must not be inside
   * a transformed or backdrop-filtered element — the HUD root is the right home, the palette panel
   * (which blurs its backdrop) is not.
   */
  host: () => HTMLElement;
  preferences: UiPreferences;
  season: () => SimpleSeason;
  /**
   * The day the scene is actually showing, which drives the 3D preview's foliage the same way it
   * drives the viewport's. Falls back to the current season's anchor day when not supplied.
   */
  dayOfYear?: () => number;
  /** Arm this plant for placement. Omitted when the card is opened somewhere read-only. */
  onPick?: (definitionId: string) => void;
  /** Favorites changed from inside the card, so the palette can redraw its stars. */
  onFavoriteChange?: () => void;
  /** The card opened or closed — including by Escape or a click elsewhere. */
  onOpenChange?: () => void;
  /**
   * The site's flora resolver. Given one, the turntable and the sheet both read this site's
   * calendar, so the card can never disagree with the canvas behind it.
   */
  resolver?: () => FloraResolver | null;
}

/** Card width in pixels, mirrored in `hud.css`; used to decide which side of the palette it opens on. */
const CARD_WIDTH = 384;
const EDGE_MARGIN = 10;
/** Aspect the turntable is drawn at — wide enough to hold a tree and its figure side by side. */
const STAGE_ASPECT = 4 / 3;

export class FloraCard {
  readonly root: HTMLElement;

  private definition: FloraDefinition | null = null;
  private anchor: HTMLElement | null = null;
  private mounted = false;
  /**
   * Built on the first open, never before: it costs a WebGL context, and a session that never opens
   * a record should not be paying for one.
   */
  private preview: FloraPreview | null = null;
  private darkMedia: MediaQueryList | null = null;

  private readonly body: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly subtitleEl: HTMLElement;
  private readonly starButton: HTMLButtonElement;
  private readonly placeButton: HTMLButtonElement;

  /** Escape closes the card before anything else claims the key (the editor clears selection on it). */
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.stopPropagation();
    event.preventDefault();
    this.close();
  };

  /**
   * A press anywhere outside dismisses — except on the tile's own ⓘ, which would otherwise close
   * here and immediately reopen from its click handler.
   */
  private readonly onPointerDown = (event: PointerEvent): void => {
    if (!this.isOpen) return;
    const target = event.target as Node | null;
    if (target && (this.root.contains(target) || this.anchor?.contains(target))) return;
    this.close();
  };

  /** The palette scrolls under the card, so the card follows its tile — or leaves once it is gone. */
  private readonly onReflow = (): void => {
    if (!this.isOpen) return;
    this.position();
  };

  /** The HUD follows the system theme, and so must the turntable's backdrop. */
  private readonly onThemeChange = (): void => {
    this.preview?.setTheme(this.theme);
  };

  constructor(private readonly deps: FloraCardDeps) {
    this.titleEl = el('h2', { class: 'lg-card__title' });
    this.subtitleEl = el('span', { class: 'lg-card__subtitle' });
    this.body = el('div', { class: 'lg-card__body' });

    this.starButton = el('button', {
      class: 'lg-card__iconbutton',
      attrs: { type: 'button', 'aria-label': 'Toggle favorite' },
      on: { click: () => this.toggleFavorite() },
    });

    this.placeButton = el('button', {
      class: 'lg-card__place',
      attrs: { type: 'button' },
      title: 'Arm this plant for placement',
      on: {
        click: () => {
          if (this.definition) this.deps.onPick?.(this.definition.id);
        },
      },
    });
    this.placeButton.append(
      el('span', { class: 'lg-card__placeicon', html: icon('plant', 14) }),
      el('span', { text: 'Place' }),
    );

    this.root = el(
      'section',
      {
        class: 'lg-card',
        attrs: { role: 'dialog', 'aria-label': 'Plant record', hidden: true },
      },
      el(
        'header',
        { class: 'lg-card__head' },
        el(
          'div',
          { class: 'lg-card__heading' },
          this.titleEl,
          this.subtitleEl,
        ),
        this.starButton,
        el('button', {
          class: 'lg-card__iconbutton',
          html: icon('close', 15),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the plant record' },
          on: { click: () => this.close() },
        }),
      ),
      this.body,
      el('footer', { class: 'lg-card__foot' }, this.placeButton),
    );
  }

  get isOpen(): boolean {
    return this.definition !== null && this.root.hidden === false;
  }

  get openId(): string | null {
    return this.isOpen ? this.definition?.id ?? null : null;
  }

  /** Open on `definition`, parked beside `anchor`. Opening on the plant already shown re-renders it. */
  open(definition: FloraDefinition, anchor: HTMLElement): void {
    this.mount();
    this.definition = definition;
    this.anchor = anchor;
    this.root.hidden = false;
    this.render();
    this.position();
    this.body.scrollTop = 0;
    this.showPreview();
    this.deps.onOpenChange?.();
  }

  /** Point the open card at a freshly rendered anchor for the same plant. */
  reanchor(anchor: HTMLElement): void {
    if (!this.isOpen) return;
    this.anchor = anchor;
    this.position();
    this.deps.onOpenChange?.();
  }

  /** The ⓘ toggles: pressing it again on the plant already open closes the card. */
  toggle(definition: FloraDefinition, anchor: HTMLElement): void {
    if (this.openId === definition.id) this.close();
    else this.open(definition, anchor);
  }

  close(): void {
    if (!this.isOpen) return;
    this.root.hidden = true;
    const anchor = this.anchor;
    this.definition = null;
    this.anchor = null;
    // The turntable is the one part of this card that costs anything while nobody is looking.
    this.preview?.stop();
    this.deps.onOpenChange?.();
    anchor?.focus();
  }

  /** Redraw for outside changes — the season moved, or units were switched. */
  refresh(): void {
    if (!this.isOpen) return;
    this.render();
    this.position();
    this.showPreview();
  }

  private mount(): void {
    if (this.mounted) return;
    this.deps.host().appendChild(this.root);
    // Capture phase for the same reason the manifest uses it: re-rendering the card replaces the
    // element that had focus, and a listener hung off the card would then miss the key entirely.
    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('pointerdown', this.onPointerDown, true);
    window.addEventListener('resize', this.onReflow);
    // The palette body is the thing that actually scrolls under the card.
    window.addEventListener('scroll', this.onReflow, true);
    this.mounted = true;
  }

  /* ------------------------------------- Turntable ---------------------------------- */

  private get theme(): 'light' | 'dark' {
    return this.darkMedia?.matches ? 'dark' : 'light';
  }

  /** The day the preview should grow its foliage for. */
  private get dayOfYear(): number {
    const supplied = this.deps.dayOfYear?.();
    if (supplied !== undefined) return supplied;
    const season = this.deps.season();
    return SEASON_ANCHORS.find((anchor) => anchor.season === season)?.day ?? 172;
  }

  /** The metre grid the turntable will draw for the plant on show — named in the caption. */
  private get gridSpacingMeters(): number {
    const definition = this.definition;
    if (!definition) return 1;
    return frameSubject(
      {
        heightMeters: definition.growth.matureHeightMeters.typical,
        spreadMeters: definition.growth.matureSpreadMeters.typical,
      },
      STAGE_ASPECT,
    ).gridSpacing;
  }

  private ensurePreview(): FloraPreview | null {
    if (this.preview) return this.preview;
    const preview = new FloraPreview();
    if (!preview.available) {
      // Keep the instance so we do not retry a context that is not coming, but the card will now
      // draw its fallback figure every render.
      preview.dispose();
      this.preview = preview;
      return preview;
    }
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.darkMedia = window.matchMedia('(prefers-color-scheme: dark)');
      this.darkMedia.addEventListener('change', this.onThemeChange);
    }
    preview.setTheme(this.theme);
    this.preview = preview;
    return preview;
  }

  /** Put the open plant on the turntable and size the canvas to the stage it landed in. */
  private showPreview(): void {
    const preview = this.preview;
    const definition = this.definition;
    if (!preview?.available || !definition) return;

    const stage = preview.canvas?.parentElement;
    const width = stage?.clientWidth ?? CARD_WIDTH - 24;
    preview.setSize(width, Math.round(width / STAGE_ASPECT));
    preview.show(definition, this.dayOfYear, this.deps.resolver?.() ?? undefined);
  }

  private toggleFavorite(): void {
    if (!this.definition) return;
    this.deps.preferences.toggleFavorite(this.definition.id);
    this.renderStar();
    this.deps.onFavoriteChange?.();
  }

  private renderStar(): void {
    const favorite = this.definition
      ? this.deps.preferences.isFavorite(this.definition.id)
      : false;
    this.starButton.innerHTML = favorite ? filledIcon('star', 15) : icon('star', 15);
    this.starButton.classList.toggle('is-active', favorite);
    this.starButton.title = favorite ? 'Remove from favorites' : 'Add to favorites';
  }

  /* ----------------------------------- Positioning ---------------------------------- */

  /**
   * Park the card beside the palette, level with its tile: outboard of the panel if there is room,
   * flipped to the other side if there is not. Anchoring horizontally to the panel rather than to
   * the tile keeps the card's left edge still while you move down a column of tiles, so only the
   * content changes; vertically it is held inside the palette's own band, which keeps it clear of
   * the toolbar above and the status bar below without either measurement being hard-coded here.
   */
  private position(): void {
    const anchor = this.anchor;
    if (!anchor?.isConnected) return;

    const anchorRect = anchor.getBoundingClientRect();
    const panel = anchor.closest('.lg-panel');
    const panelRect = panel ? panel.getBoundingClientRect() : anchorRect;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const rightOfPanel = panelRect.right + EDGE_MARGIN;
    const leftOfPanel = panelRect.left - CARD_WIDTH - EDGE_MARGIN;
    const left =
      rightOfPanel + CARD_WIDTH + EDGE_MARGIN <= viewportWidth
        ? rightOfPanel
        : Math.max(EDGE_MARGIN, leftOfPanel);

    const bandTop = panel ? panelRect.top : EDGE_MARGIN;
    const bandBottom = panel ? panelRect.bottom : viewportHeight - EDGE_MARGIN;

    // Cap first, then measure: the card is usually taller than the band, and its height only
    // settles once the cap is applied.
    this.root.style.maxHeight = `${Math.round(Math.max(160, bandBottom - bandTop))}px`;
    const height = this.root.offsetHeight;
    // Hang it from the tile, then slide it up if that would run past the bottom of the band.
    const top = Math.max(bandTop, Math.min(anchorRect.top - 4, bandBottom - height));

    this.root.style.left = `${Math.round(left)}px`;
    this.root.style.top = `${Math.round(top)}px`;
  }

  /* ------------------------------------ Rendering ----------------------------------- */

  private render(): void {
    const definition = this.definition;
    if (!definition) return;

    const unit = this.deps.preferences.unit;
    const season = this.deps.season();
    // Resolved against the site when there is one, which is what adds the "This site" block and
    // dates the ecology rows. Without a site the sheet is exactly the record, as it always was.
    const resolver = this.deps.resolver?.() ?? null;
    const sheet = buildFloraFactsheet(
      definition,
      unit,
      resolver ? resolver.resolve(definition) : undefined,
    );

    this.titleEl.textContent = sheet.title;
    this.subtitleEl.textContent = sheet.botanicalName;
    this.renderStar();
    this.placeButton.hidden = this.deps.onPick === undefined;

    clear(this.body);
    append(
      this.body,
      this.identityBlock(definition, sheet, season),
      this.seasonBlock(definition, sheet),
      this.yearBlock(sheet),
      this.growthBlock(sheet, unit),
      this.stageBlock(sheet, unit),
      this.scaleBlock(definition, sheet, unit, season),
      ...sheet.sections.map((section) => this.factBlock(section)),
      this.rawBlock(definition),
    );
  }

  /** Portrait, taxonomy, and the chips the palette filters on. */
  private identityBlock(
    definition: FloraDefinition,
    sheet: FloraFactsheet,
    season: SimpleSeason,
  ): HTMLElement {
    // The evergreen flag earns a chip only when the tags do not already say so — several records
    // carry both, and printing them twice reads as a bug rather than as emphasis.
    const taggedEvergreen = sheet.tags.some((tag) => tag.toLowerCase() === 'evergreen');
    const chips: Child[] = [
      el('span', { class: 'lg-card__chip lg-card__chip--strong', text: sheet.formFactorLabel }),
      el('span', { class: 'lg-card__chip', text: sheet.categoryLabel }),
      sheet.evergreen && !taggedEvergreen
        ? el('span', { class: 'lg-card__chip', text: 'Evergreen' })
        : null,
      ...sheet.tags.map((tag) => el('span', { class: 'lg-card__chip', text: tag.replace(/-/g, ' ') })),
    ];

    const families = el('div', { class: 'lg-card__families' });
    for (const family of sheet.colorFamilies) {
      families.appendChild(
        el(
          'span',
          { class: 'lg-card__family', title: `${family.label} appears in this plant’s year` },
          el('span', { class: 'lg-card__familydot', style: { background: family.swatch } }),
          el('span', { text: family.label }),
        ),
      );
    }

    return el(
      'section',
      { class: 'lg-card__block lg-card__block--identity' },
      el('div', {
        class: 'lg-card__portrait',
        html: floraThumbnailSvg(definition, { season }),
        title: `Silhouette as drawn for ${season}`,
      }),
      el(
        'div',
        { class: 'lg-card__identity' },
        el('div', { class: 'lg-card__names' },
          el('span', { class: 'lg-card__latin', text: sheet.botanicalName }),
          sheet.commonNames.length > 0
            ? el('span', { class: 'lg-card__common', text: sheet.commonNames.join(' · ') })
            : null,
        ),
        el('div', { class: 'lg-card__chips' }, ...chips),
        families,
      ),
    );
  }

  /** The four seasonal appearance blocks, drawn and tabulated side by side. */
  private seasonBlock(definition: FloraDefinition, sheet: FloraFactsheet): HTMLElement {
    const grid = el('div', { class: 'lg-card__seasons' });
    for (const facet of sheet.seasons) {
      grid.appendChild(this.seasonTile(definition, facet));
    }
    return this.block('Seasonal appearance', 'One appearance block per season, as the record stores them.', grid);
  }

  private seasonTile(definition: FloraDefinition, facet: SeasonFacet): HTMLElement {
    const swatches = el('div', { class: 'lg-card__seasonswatches' });
    swatches.appendChild(this.colorChip('Foliage', facet.foliageColor, facet.foliageDensity));
    if (facet.flowerColor) {
      swatches.appendChild(this.colorChip('Flower', facet.flowerColor, facet.flowerDensity));
    }
    if (facet.fruitColor) {
      swatches.appendChild(this.colorChip('Fruit', facet.fruitColor, facet.fruitDensity));
    }

    return el(
      'div',
      { class: `lg-card__season${facet.dormant ? ' is-dormant' : ''}` },
      el(
        'div',
        { class: 'lg-card__seasonhead' },
        el('span', { class: 'lg-card__seasonname', text: facet.label }),
        facet.dormant ? el('span', { class: 'lg-card__seasonflag', text: 'dormant' }) : null,
      ),
      el('div', {
        class: 'lg-card__seasonthumb',
        html: floraThumbnailSvg(definition, { season: facet.season, chrome: false }),
      }),
      swatches,
      this.meterRow('Foliage', facet.foliageDensity),
      this.meterRow('Branches', facet.branchVisibility),
    );
  }

  private colorChip(label: string, color: string, density: number): HTMLElement {
    return el(
      'span',
      {
        class: 'lg-card__colorchip',
        title: `${label} ${color.toUpperCase()} · ${percentLabel(density)} density`,
      },
      el('span', { class: 'lg-card__colordot', style: { background: safeColor(color, '#888') } }),
      el('span', { class: 'lg-card__colorhex', text: color.toUpperCase() }),
    );
  }

  private meterRow(label: string, value: number): HTMLElement {
    return el(
      'div',
      { class: 'lg-card__meterrow' },
      el('span', { class: 'lg-card__meterlabel', text: label }),
      el(
        'span',
        { class: 'lg-card__meter' },
        el('span', {
          class: 'lg-card__meterfill',
          style: { width: `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%` },
        }),
      ),
      el('span', { class: 'lg-card__metervalue', text: percentLabel(value) }),
    );
  }

  /** Bloom windows and leaf phenology, plotted on one year. Absent from evergreens with no bloom. */
  private yearBlock(sheet: FloraFactsheet): HTMLElement | null {
    if (sheet.bloomBands.length === 0 && sheet.yearEvents.length === 0) return null;

    const legend = el('div', { class: 'lg-card__legend' });
    for (const band of sheet.bloomBands) {
      legend.appendChild(
        el(
          'span',
          { class: 'lg-card__legenditem', title: band.label },
          el('span', {
            class: 'lg-card__legendswatch',
            style: { background: safeColor(band.color, '#c9a227') },
          }),
          el('span', { text: `Bloom · ${band.label}` }),
        ),
      );
    }
    for (const event of sheet.yearEvents) {
      legend.appendChild(
        el(
          'span',
          { class: 'lg-card__legenditem' },
          el('span', { class: `lg-card__legendtick is-${event.kind}` }),
          el('span', { text: `${event.label} · ${event.dateLabel}` }),
        ),
      );
    }

    return this.block(
      'Year',
      'When this plant blooms, leafs out, colours, and drops.',
      el('div', { class: 'lg-card__figure', html: yearStripSvg(sheet) }),
      legend,
    );
  }

  /** Height and spread against maturity, run through the real growth model. */
  private growthBlock(sheet: FloraFactsheet, unit: 'meters' | 'feet'): HTMLElement {
    return this.block(
      'Growth curve',
      'Height and spread follow their own easing, so maturity is never a uniform scale.',
      el('div', { class: 'lg-card__figure', html: growthPlotSvg(sheet, unit) }),
      el(
        'div',
        { class: 'lg-card__legend' },
        el(
          'span',
          { class: 'lg-card__legenditem' },
          el('span', { class: 'lg-card__legendline is-height' }),
          el('span', { text: `Height · ${sheet.heightCurveLabel}` }),
        ),
        el(
          'span',
          { class: 'lg-card__legenditem' },
          el('span', { class: 'lg-card__legendline is-spread' }),
          el('span', { text: `Spread · ${sheet.spreadCurveLabel}` }),
        ),
      ),
    );
  }

  /** The nursery stages, which are what actually gets ordered. */
  private stageBlock(sheet: FloraFactsheet, unit: 'meters' | 'feet'): HTMLElement {
    if (sheet.stages.length === 0) {
      return this.block(
        'Nursery stages',
        'This record carries no container stages.',
        el('p', { class: 'lg-card__empty', text: 'No stages defined — the plant starts from the model’s seedling floor.' }),
      );
    }

    const rows = sheet.stages.map((stage) =>
      el(
        'tr',
        {},
        el('td', { class: 'lg-card__stagename', text: stage.label }),
        el('td', { text: `${numberLabel(stage.ageYears, 1)} yr` }),
        el('td', { text: formatLength(stage.heightMeters, unit) }),
        el('td', { text: formatLength(stage.spreadMeters, unit) }),
        el('td', {},
          el('span', { class: 'lg-card__stagebar' },
            el('span', {
              class: 'lg-card__stagefill',
              style: { width: `${Math.round(Math.min(1, stage.fractionOfMature) * 100)}%` },
            }),
          ),
          el('span', { class: 'lg-card__stagepct', text: percentLabel(stage.fractionOfMature) }),
        ),
      ),
    );

    const head = el('tr');
    for (const heading of ['Stage', 'Age', 'Height', 'Spread', 'Of mature']) {
      head.appendChild(el('th', { text: heading, attrs: { scope: 'col' } }));
    }
    const bodyRows = el('tbody');
    for (const row of rows) bodyRows.appendChild(row);

    return this.block(
      'Nursery stages',
      'Container sizes this plant can be specified and priced at.',
      el('table', { class: 'lg-card__table' }, el('thead', {}, head), bodyRows),
    );
  }

  /**
   * Mature size against a person, so the number means something.
   *
   * The live turntable is the real answer — the actual mesh, at maturity, on a metre grid — and the
   * drawn figure is what a browser with no WebGL context gets instead. Both are built here rather
   * than one replacing the other, because the fallback has to be a first-class drawing: a catalog
   * that shows a blank rectangle on a locked-down machine is worse than one that never promised 3D.
   */
  private scaleBlock(
    definition: FloraDefinition,
    sheet: FloraFactsheet,
    unit: 'meters' | 'feet',
    season: SimpleSeason,
  ): HTMLElement {
    const preview = this.ensurePreview();
    const figure =
      preview?.canvas
        ? el(
            'div',
            { class: 'lg-card__stage' },
            preview.canvas,
            el('div', { class: 'lg-card__stagehint', text: 'Drag to orbit · scroll to zoom' }),
            el('button', {
              class: 'lg-card__stagereset',
              html: icon('focus', 13),
              title: 'Reset the view  ·  or double-click the model',
              attrs: { type: 'button', 'aria-label': 'Reset the preview camera' },
              on: {
                click: (event) => {
                  event.stopPropagation();
                  preview.resetView();
                },
              },
            }),
          )
        : el('div', {
            class: 'lg-card__figure',
            html: scaleFigureSvg(definition, sheet, unit, season),
          });

    const caption = [
      `${formatLength(sheet.matureHeightMeters, unit)} tall`,
      `${formatLength(sheet.matureSpreadMeters, unit)} across`,
      preview?.canvas ? `${formatLength(this.gridSpacingMeters, unit)} grid` : null,
      `figure ${formatLength(PERSON_HEIGHT_METERS, unit)}`,
    ]
      .filter((part): part is string => part !== null)
      .join('  ·  ');

    return this.block(
      'Mature scale',
      preview?.canvas
        ? 'The built model at maturity, in this season. Drag it around.'
        : `Grown, against a ${formatLength(PERSON_HEIGHT_METERS, unit)} figure.`,
      figure,
      el('p', { class: 'lg-card__caption', text: caption }),
    );
  }

  /** One section of the record, as label/value rows. */
  private factBlock(section: FactSection): HTMLElement {
    const list = el('dl', { class: 'lg-card__facts' });
    for (const item of section.facts) list.append(...this.factRow(item));
    return this.block(section.title, section.note, list);
  }

  private factRow(item: Fact): [HTMLElement, HTMLElement] {
    const term = el('dt', {
      class: `lg-card__factlabel${item.absent ? ' is-absent' : ''}`,
      text: item.label,
      ...(item.hint ? { title: item.hint } : {}),
    });

    const value = el('dd', { class: `lg-card__factvalue${item.absent ? ' is-absent' : ''}` });
    if (item.color) {
      value.appendChild(
        el('span', { class: 'lg-card__factswatch', style: { background: safeColor(item.color, '#888') } }),
      );
    }
    value.appendChild(el('span', { text: item.value }));
    if (item.meter !== null) {
      value.appendChild(
        el('span', { class: 'lg-card__factmeter' },
          el('span', {
            class: 'lg-card__factmeterfill',
            style: { width: `${Math.round(Math.min(1, Math.max(0, item.meter)) * 100)}%` },
          }),
        ),
      );
    }
    return [term, value];
  }

  /**
   * The record itself. Everything above is a reading of this; when the two disagree, this is the
   * one that is true, so it is worth being able to see. Set as text, never as markup.
   */
  private rawBlock(definition: FloraDefinition): HTMLElement {
    const pre = el('pre', { class: 'lg-card__raw' });
    pre.textContent = JSON.stringify(definition, null, 2);
    return el(
      'details',
      { class: 'lg-card__block lg-card__details' },
      el('summary', { class: 'lg-card__summary', text: 'Raw catalog record' }),
      pre,
    );
  }

  private block(title: string, note: string, ...children: Child[]): HTMLElement {
    return el(
      'section',
      { class: 'lg-card__block' },
      el('h3', { class: 'lg-card__blocktitle', text: title }),
      note ? el('p', { class: 'lg-card__blocknote', text: note }) : null,
      ...children,
    );
  }

  dispose(): void {
    if (this.mounted) {
      window.removeEventListener('keydown', this.onKeyDown, true);
      window.removeEventListener('pointerdown', this.onPointerDown, true);
      window.removeEventListener('resize', this.onReflow);
      window.removeEventListener('scroll', this.onReflow, true);
      this.mounted = false;
    }
    this.darkMedia?.removeEventListener('change', this.onThemeChange);
    this.darkMedia = null;
    this.preview?.dispose();
    this.preview = null;
    this.definition = null;
    this.anchor = null;
    this.root.remove();
  }
}

/* ------------------------------------- Figures ------------------------------------- */

/*
 * The three drawings are plain SVG strings rather than elements: they are pure functions of the
 * factsheet, they are rebuilt whole on every render, and building them as markup keeps the shape of
 * each figure readable in one place. Every interpolated value below is either a number this module
 * computed or a colour put through `safeColor`.
 */

const MONTH_STARTS = [1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];
const MONTH_INITIALS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const EVENT_COLORS: Record<string, string> = {
  'leaf-out': '#6cc26a',
  'autumn-color': '#dd7a2c',
  'leaf-drop': '#8a94a1',
};

/** The plant's year: bloom windows as bands, leaf phenology as ticks, months underneath. */
function yearStripSvg(sheet: FloraFactsheet): string {
  const width = 340;
  const height = 52;
  const barTop = 6;
  const barHeight = 22;
  const x = (day: number): number => (Math.max(1, Math.min(365, day)) / 365) * width;

  const months = MONTH_STARTS.map(
    (day, index) =>
      `<line x1="${x(day).toFixed(1)}" y1="${barTop}" x2="${x(day).toFixed(1)}" y2="${barTop + barHeight}" ` +
      `stroke="currentColor" stroke-width="1" opacity="0.14"/>` +
      `<text x="${(x(day) + 2).toFixed(1)}" y="${height - 6}" font-size="8" fill="currentColor" opacity="0.5">` +
      `${MONTH_INITIALS[index] ?? ''}</text>`,
  ).join('');

  const bands = sheet.bloomBands
    .map((band) => {
      // A window that wraps past new year is drawn as the two pieces it actually covers.
      const spans =
        band.endDay >= band.startDay
          ? [[band.startDay, band.endDay]]
          : [
              [band.startDay, 365],
              [1, band.endDay],
            ];
      const color = safeColor(band.color, '#c9a227');
      return spans
        .map(([start, end]) => {
          const left = x(start ?? 1);
          const right = x(end ?? 365);
          return (
            `<rect x="${left.toFixed(1)}" y="${barTop}" width="${Math.max(2, right - left).toFixed(1)}" ` +
            `height="${barHeight}" rx="3" fill="${color}" opacity="${(0.35 + 0.6 * band.intensity).toFixed(2)}"/>`
          );
        })
        .join('');
    })
    .join('');

  const events = sheet.yearEvents
    .map((event) => {
      const at = x(event.day).toFixed(1);
      const color = EVENT_COLORS[event.kind] ?? 'currentColor';
      return (
        `<line x1="${at}" y1="${barTop - 3}" x2="${at}" y2="${barTop + barHeight + 3}" stroke="${color}" stroke-width="2"/>` +
        `<circle cx="${at}" cy="${barTop - 4}" r="2.4" fill="${color}"/>`
      );
    })
    .join('');

  return (
    `<svg class="lg-card__svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="Bloom and leaf calendar">` +
    `<rect x="0" y="${barTop}" width="${width}" height="${barHeight}" rx="4" fill="currentColor" opacity="0.07"/>` +
    `${bands}${months}${events}</svg>`
  );
}

/** Height and spread against maturity, with the nursery stages marked on the height curve. */
function growthPlotSvg(sheet: FloraFactsheet, unit: 'meters' | 'feet'): string {
  const width = 340;
  const height = 132;
  const padLeft = 4;
  const padRight = 4;
  const padTop = 10;
  const baseline = height - 18;

  const peak = Math.max(
    sheet.matureHeightMeters,
    sheet.matureSpreadMeters,
    ...sheet.curve.map((sample) => Math.max(sample.heightMeters, sample.spreadMeters)),
    0.1,
  );
  const plotWidth = width - padLeft - padRight;
  const x = (maturity: number): number => padLeft + maturity * plotWidth;
  const y = (meters: number): number => baseline - (meters / peak) * (baseline - padTop);

  const path = (pick: (sample: { heightMeters: number; spreadMeters: number }) => number): string =>
    sheet.curve
      .map((sample, index) => `${index === 0 ? 'M' : 'L'}${x(sample.maturity).toFixed(1)} ${y(pick(sample)).toFixed(1)}`)
      .join(' ');

  const heightPath = path((sample) => sample.heightMeters);
  const spreadPath = path((sample) => sample.spreadMeters);

  const stageMarks = sheet.stages
    .map((stage) => {
      // Stages are height anchors, so they are placed by where their height meets the curve.
      const sample = sheet.curve.find((point) => point.heightMeters >= stage.heightMeters);
      const maturity = sample ? sample.maturity : 1;
      return (
        `<circle cx="${x(maturity).toFixed(1)}" cy="${y(stage.heightMeters).toFixed(1)}" r="3" ` +
        `fill="var(--lg-accent)" stroke="var(--lg-bg-solid)" stroke-width="1.5"><title>${escapeText(stage.label)} · ` +
        `${escapeText(formatLength(stage.heightMeters, unit))}</title></circle>`
      );
    })
    .join('');

  const gridLines = [0.25, 0.5, 0.75, 1]
    .map(
      (fraction) =>
        `<line x1="${padLeft}" y1="${y(peak * fraction).toFixed(1)}" x2="${width - padRight}" ` +
        `y2="${y(peak * fraction).toFixed(1)}" stroke="currentColor" stroke-width="1" opacity="0.08"/>`,
    )
    .join('');

  return (
    `<svg class="lg-card__svg" viewBox="0 0 ${width} ${height}" role="img" ` +
    `aria-label="Height and spread against maturity">` +
    gridLines +
    `<line x1="${padLeft}" y1="${baseline}" x2="${width - padRight}" y2="${baseline}" stroke="currentColor" opacity="0.25"/>` +
    `<path d="${heightPath} L${x(1).toFixed(1)} ${baseline} L${padLeft} ${baseline} Z" fill="var(--lg-accent)" opacity="0.12"/>` +
    `<path d="${spreadPath}" fill="none" stroke="#7aa7d8" stroke-width="2" stroke-dasharray="4 3" stroke-linecap="round"/>` +
    `<path d="${heightPath}" fill="none" stroke="var(--lg-accent)" stroke-width="2.2" stroke-linecap="round"/>` +
    stageMarks +
    `<text x="${padLeft}" y="${height - 5}" font-size="8.5" fill="currentColor" opacity="0.55">Nursery</text>` +
    `<text x="${width - padRight}" y="${height - 5}" font-size="8.5" fill="currentColor" opacity="0.55" ` +
    `text-anchor="end">Mature · ${escapeText(formatLength(peak, unit))}</text>` +
    `</svg>`
  );
}

/**
 * Mature size beside a person. The plant is the same silhouette the palette draws, nested and
 * stretched to its real height-to-spread proportions — the drawing's own plant occupies roughly the
 * middle 36 units and the top 52 of its 64-unit box, which is what the two ratios below correct for.
 */
function scaleFigureSvg(
  definition: FloraDefinition,
  sheet: FloraFactsheet,
  unit: 'meters' | 'feet',
  season: SimpleSeason,
): string {
  const width = 340;
  const height = 150;
  const ground = height - 20;
  const top = 12;

  const tallest = Math.max(sheet.matureHeightMeters, PERSON_HEIGHT_METERS);
  const scale = (ground - top) / (tallest * 1.06);
  const plantHeightPx = sheet.matureHeightMeters * scale;
  const plantWidthPx = Math.max(12, sheet.matureSpreadMeters * scale);

  // Undo the silhouette's own margins so the drawn plant, not its box, matches the real dimensions.
  const boxHeight = plantHeightPx * (64 / 52);
  const boxWidth = plantWidthPx * (64 / 36);
  const boxLeft = width * 0.42 - boxWidth / 2;
  const boxTop = ground - boxHeight * (56 / 64);

  const personHeightPx = PERSON_HEIGHT_METERS * scale;
  const personX = width - 40;

  const thumb = floraThumbnailSvg(definition, { season, chrome: false });
  const nested =
    `<svg x="${boxLeft.toFixed(1)}" y="${boxTop.toFixed(1)}" width="${boxWidth.toFixed(1)}" ` +
    `height="${boxHeight.toFixed(1)}" viewBox="0 0 64 64" preserveAspectRatio="none">` +
    `${thumb.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '')}</svg>`;

  const person = personFigureSvg(personX, ground, personHeightPx);

  const dimension =
    `<line x1="14" y1="${(ground - plantHeightPx).toFixed(1)}" x2="14" y2="${ground}" stroke="currentColor" ` +
    `opacity="0.35" stroke-width="1"/>` +
    `<line x1="10" y1="${(ground - plantHeightPx).toFixed(1)}" x2="18" y2="${(ground - plantHeightPx).toFixed(1)}" ` +
    `stroke="currentColor" opacity="0.35" stroke-width="1"/>` +
    `<text x="20" y="${(ground - plantHeightPx + 9).toFixed(1)}" font-size="9" fill="currentColor" opacity="0.7">` +
    `${escapeText(formatLength(sheet.matureHeightMeters, unit))} tall</text>`;

  const spreadRule =
    `<line x1="${boxLeft + (boxWidth - plantWidthPx) / 2}" y1="${ground + 6}" ` +
    `x2="${boxLeft + (boxWidth + plantWidthPx) / 2}" y2="${ground + 6}" stroke="currentColor" opacity="0.35"/>` +
    `<text x="${(width * 0.42).toFixed(1)}" y="${height - 3}" font-size="9" fill="currentColor" opacity="0.7" ` +
    `text-anchor="middle">${escapeText(formatLength(sheet.matureSpreadMeters, unit))} across</text>`;

  return (
    `<svg class="lg-card__svg" viewBox="0 0 ${width} ${height}" role="img" ` +
    `aria-label="Mature size compared with a person">` +
    `<line x1="0" y1="${ground}" x2="${width}" y2="${ground}" stroke="currentColor" opacity="0.25"/>` +
    nested +
    person +
    dimension +
    spreadRule +
    `</svg>`
  );
}

/** A plain scale figure — head, body, legs — drawn to `heightPx` standing on `ground`. */
function personFigureSvg(x: number, ground: number, heightPx: number): string {
  const headRadius = Math.max(1.6, heightPx * 0.075);
  const headCenter = ground - heightPx + headRadius;
  const shoulder = headCenter + headRadius * 1.6;
  const hip = ground - heightPx * 0.45;
  const stroke = Math.max(1.2, heightPx * 0.055);
  return (
    `<g stroke="currentColor" opacity="0.45" stroke-width="${stroke.toFixed(2)}" stroke-linecap="round" fill="none">` +
    `<circle cx="${x}" cy="${headCenter.toFixed(1)}" r="${headRadius.toFixed(1)}" fill="currentColor"/>` +
    `<line x1="${x}" y1="${shoulder.toFixed(1)}" x2="${x}" y2="${hip.toFixed(1)}"/>` +
    `<line x1="${x}" y1="${(shoulder + heightPx * 0.06).toFixed(1)}" x2="${(x - heightPx * 0.12).toFixed(1)}" y2="${(hip - heightPx * 0.02).toFixed(1)}"/>` +
    `<line x1="${x}" y1="${(shoulder + heightPx * 0.06).toFixed(1)}" x2="${(x + heightPx * 0.12).toFixed(1)}" y2="${(hip - heightPx * 0.02).toFixed(1)}"/>` +
    `<line x1="${x}" y1="${hip.toFixed(1)}" x2="${(x - heightPx * 0.1).toFixed(1)}" y2="${ground}"/>` +
    `<line x1="${x}" y1="${hip.toFixed(1)}" x2="${(x + heightPx * 0.1).toFixed(1)}" y2="${ground}"/>` +
    `</g>`
  );
}

/** Escape the few characters that would end an SVG text node or attribute early. */
function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
